import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Grid,
  IconButton,
  InputBase,
  makeStyles,
  Typography,
} from "@material-ui/core";
import { Add, CheckCircle, Close, InfoOutlined, Remove, Warning } from "@material-ui/icons";
import { Alert } from "@material-ui/lab";
import { SubmitButton } from "@microrealestate/commonui/components";
import { Form, Formik, useFormikContext } from "formik";
import { toJS } from "mobx";
import { observer } from "mobx-react-lite";
import moment from "moment";
import useTranslation from "next-translate/useTranslation";
import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import useFormatNumber from "../../../hooks/useFormatNumber";
import { StoreContext } from "../../../store";
import {
  SUBSCRIPTION_PLANS as STATIC_PLANS,
  SUBSCRIPTION_CHANGE_TYPE,
} from "../../../utils/constants/subscription.constant";
import DelayedRoomSelector from "./DelayedRoomSelector";
import {
  calculateProrationByExactDays,
  calculateProrationValue,
} from "../../../utils/calculations/subscription.prorate";

const LIMIT_MIN = 1;
const LIMIT_MAX = 99999;

const useStyles = makeStyles((theme) => ({
  root: {
    padding: 0,
  },
  sectionTitle: {
    marginBottom: theme.spacing(2),
    fontWeight: 700,
    fontSize: "0.9rem",
    color: theme.palette.text.primary,
    textTransform: "uppercase",
    letterSpacing: "0.05em",
  },
  planGrid: {
    display: "grid",
    gap: theme.spacing(2),
    gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))",
    marginBottom: theme.spacing(3),
  },
  planOption: {
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
    width: "100%",
    padding: theme.spacing(2),
    borderRadius: 12,
    border: `2px solid ${theme.palette.divider}`,
    backgroundColor: theme.palette.background.paper,
    textAlign: "left",
    textTransform: "none",
    position: "relative",
    transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
    "&:hover": {
      borderColor: theme.palette.primary.light,
      backgroundColor: "rgba(25, 118, 210, 0.02)",
    },
  },
  planOptionLabel: {
    display: "flex",
    flexDirection: "column",
  },
  planOptionSelected: {
    borderColor: theme.palette.primary.main,
    backgroundColor: "rgba(25, 118, 210, 0.04)",
    boxShadow: "0 4px 12px rgba(25, 118, 210, 0.08)",
  },
  planOptionName: {
    fontWeight: 800,
    fontSize: "1rem",
    marginBottom: theme.spacing(0.5),
  },
  planOptionPrice: {
    fontWeight: 700,
    fontSize: "0.85rem",
    color: theme.palette.primary.main,
    marginBottom: theme.spacing(1),
  },
  planOptionMeta: {
    color: theme.palette.text.secondary,
    fontSize: "0.75rem",
    lineHeight: 1.4,
  },
  selectedBadge: {
    position: "absolute",
    top: 8,
    right: 8,
    color: theme.palette.primary.main,
  },
  currentBadge: {
    position: "absolute",
    top: -10,
    left: "50%",
    transform: "translateX(-50%)",
    backgroundColor: theme.palette.success.main,
    color: "#fff",
    padding: "2px 8px",
    borderRadius: 4,
    fontSize: "0.6rem",
    fontWeight: 900,
    textTransform: "uppercase",
  },
  periodGrid: {
    display: "grid",
    gap: theme.spacing(2),
    gridTemplateColumns: "repeat(3, 1fr)",
    marginBottom: theme.spacing(3),
  },
  limitCard: {
    padding: theme.spacing(2.5),
    backgroundColor: "#f8f9fa",
    borderRadius: 12,
    border: `1px solid ${theme.palette.divider}`,
  },
  limitStepperRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginTop: theme.spacing(1.5),
    backgroundColor: "#fff",
    borderRadius: 8,
    padding: theme.spacing(0.5),
    border: `1px solid ${theme.palette.divider}`,
  },
  limitValue: {
    flex: 1,
    textAlign: "center",
    "& input": {
      textAlign: "center",
      fontWeight: 800,
      fontSize: "1.4rem",
      color: theme.palette.primary.main,
    },
    /* Chrome, Safari, Edge, Opera */
    "& input[type=number]::-webkit-outer-spin-button, & input[type=number]::-webkit-inner-spin-button": {
      WebkitAppearance: "none",
      margin: 0,
    },
    /* Firefox */
    "& input[type=number]": {
      MozAppearance: "textfield",
    },
  },
  summaryCard: {
    padding: theme.spacing(3),
    borderRadius: 16,
    backgroundColor: "#fff",
    border: `1px solid ${theme.palette.divider}`,
    position: "sticky",
    top: theme.spacing(3),
    boxShadow: "0 8px 24px rgba(0,0,0,0.04)",
  },
  summaryTitle: {
    fontWeight: 800,
    fontSize: "1.1rem",
    marginBottom: theme.spacing(3),
    display: "flex",
    alignItems: "center",
    gap: 8,
  },
  summaryRow: {
    display: "flex",
    justifyContent: "space-between",
    marginBottom: theme.spacing(2),
    "& .label": {
      color: theme.palette.text.secondary,
      fontSize: "0.85rem",
    },
    "& .value": {
      fontWeight: 700,
      fontSize: "0.85rem",
    },
  },
  totalRow: {
    display: "flex",
    justifyContent: "space-between",
    marginTop: theme.spacing(2),
    paddingTop: theme.spacing(2),
    borderTop: `2px dashed ${theme.palette.divider}`,
    "& .label": {
      fontWeight: 800,
      fontSize: "1rem",
    },
    "& .value": {
      fontWeight: 900,
      fontSize: "1.25rem",
      color: theme.palette.primary.main,
    },
  },
  savingsChip: {
    marginTop: theme.spacing(1),
    backgroundColor: "#E8F5E9",
    color: "#2E7D32",
    fontWeight: 700,
    fontSize: "0.7rem",
    height: 24,
  },
  noteSection: {
    marginTop: theme.spacing(2),
    padding: theme.spacing(1.5),
    backgroundColor: "#F1F5F9",
    borderRadius: 8,
    borderLeft: `3px solid ${theme.palette.primary.main}`,
  },
  noteText: {
    fontSize: "0.75rem",
    color: theme.palette.text.secondary,
    lineHeight: 1.6,
    fontStyle: "italic",
  },
  noteTitle: {
    fontSize: "0.75rem",
    fontWeight: 800,
    color: theme.palette.text.primary,
    marginBottom: theme.spacing(0.5),
    display: "flex",
    alignItems: "center",
    gap: 4,
  },
  promoLabel: {
    fontWeight: 700,
    fontSize: "0.8rem",
    color: theme.palette.primary.main,
    cursor: "pointer",
    display: "inline-flex",
    alignItems: "center",
    gap: 4,
    marginTop: theme.spacing(1),
    "&:hover": {
      textDecoration: "underline",
    },
  },
  promoInputContainer: {
    display: "flex",
    gap: theme.spacing(1),
    marginTop: theme.spacing(1),
  },
  promoInput: {
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 8,
    padding: "6px 12px",
    fontSize: "0.85rem",
    flex: 1,
    backgroundColor: "#fff",
    "& input": {
      fontSize: "0.85rem",
    },
  },
  promoApplyBtn: {
    borderRadius: 8,
    textTransform: "none",
    fontWeight: 700,
  },
  appliedPromoChip: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    backgroundColor: "rgba(46, 125, 50, 0.05)",
    padding: theme.spacing(1, 1.5),
    borderRadius: 8,
    marginTop: theme.spacing(1),
    border: "1px solid rgba(46, 125, 50, 0.2)",
  },
  appliedPromoText: {
    color: "#2E7D32",
    fontWeight: 700,
    fontSize: "0.8rem",
  },
}));

const PERIODS = [
  { label: "Monthly", frequency: "months", cycle: 1, discount: 0 },
  { label: "Semi-Annually", frequency: "months", cycle: 6, discount: 0 },
  { label: "Annually", frequency: "months", cycle: 12, discount: 0 },
];

const OrderSummary = ({ plans, subscription, periods, step4Valid = true }) => {
  const { values, setFieldValue } = useFormikContext();
  const classes = useStyles();
  const formatNumber = useFormatNumber();
  const { t } = useTranslation("common");
  const store = useContext(StoreContext);

  const [showPromoInput, setShowPromoInput] = useState(false);
  const [promoError, setPromoError] = useState("");
  const [validating, setValidating] = useState(false);

  const selectedPlan = useMemo(() => {
    return plans.find((p) => p.key === values.plan) || plans[0];
  }, [plans, values.plan]);

  const selectedPeriod = useMemo(() => {
    const periodList = periods?.length > 0 ? periods : PERIODS;
    return periodList.find((p) => p.frequency === values.frequency && p.cycle === values.cycle) || periodList[0];
  }, [periods, values.frequency, values.cycle]);

  const pricePerRoom = selectedPlan.price || 0;
  const roomCount = values.activeRoom || 0;
  const cycleMonths = selectedPeriod.cycle || 1;

  /* OLD CODE:
  const subtotal = pricePerRoom * roomCount * cycleMonths;

  // Calculate applied coupon discount
  const appliedDiscount = values.appliedDiscount;
  let promoDiscountAmount = 0;
  if (appliedDiscount) {
    if (appliedDiscount.category === "transaction_discount") {
      if (appliedDiscount.type === "percentage") {
        promoDiscountAmount = Math.round(subtotal * (appliedDiscount.value / 100));
      } else if (appliedDiscount.type === "fixed_amount") {
        promoDiscountAmount = Math.min(subtotal, appliedDiscount.value);
      }
    } else if (appliedDiscount.category === "room_discount") {
      let roomPriceDiscountAmount = 0;
      if (appliedDiscount.type === "percentage") {
        roomPriceDiscountAmount = Math.round(pricePerRoom * (appliedDiscount.value / 100));
      } else if (appliedDiscount.type === "fixed_amount") {
        roomPriceDiscountAmount = appliedDiscount.value;
      }
      const discountedPricePerRoom = Math.max(0, pricePerRoom - roomPriceDiscountAmount);
      promoDiscountAmount = (pricePerRoom - discountedPricePerRoom) * roomCount * cycleMonths;
    }
  }

  const discountAmount = promoDiscountAmount;
  const total = Math.max(0, subtotal - discountAmount);
  */

  // NEW CODE:
  const currentPlan = subscription?.plan?.toLowerCase() || "starter";
  const currentActiveRoom = Number(subscription?.usage?.activeRoom) || 0;
  const currentPlanData = plans.find((p) => p.key === currentPlan) || plans[0];
  const currentBilling = subscription?.billing;

  let processType = "new";
  if (subscription?.plan && subscription?.plan !== "starter") {
    if (currentPlan !== values.plan) {
      processType = selectedPlan.price > currentPlanData.price ? "upgrade" : "downgrade";
    } else if (currentActiveRoom !== values.activeRoom) {
      processType = values.activeRoom > currentActiveRoom ? "upsize" : "downsize";
    } else if (currentBilling?.cycle !== values.cycle) {
      processType = "term_change";
    } else {
      processType = "renewal";
    }
  }

  const subtotal = pricePerRoom * roomCount * cycleMonths;

  // Calculate Prorate if applicable
  let netAmount = subtotal;
  let prorateAmount = 0;
  let prorateChargeAmount = 0;

  if (
    (processType === "upgrade" || processType === "upsize" || processType === "term_change") &&
    subscription?.period?.end
  ) {
    const oldCycleMonths = subscription?.billing?.cycle ?? 1;
    const newCycleMonths = values?.cycle ?? oldCycleMonths;

    // Current Monthly Total (we derive this from the subscription's current billing amount)
    // If subscription.billing.amount is the total for the whole cycle (e.g. 12 months),
    // we divide by old cycle to get monthly.
    const oldPriceMonthly = (subscription.billing?.amount || 0) / oldCycleMonths;

    // Monthly Total = Unit Price * Number of Rooms
    const newPriceMonthly = selectedPlan.price * values.activeRoom;

    const prorateDetails = calculateProrationByExactDays({
      currentStartDate: subscription?.period?.start,
      currentEndDate: subscription?.period?.end,
      oldPriceMonthly,
      newPriceMonthly,
      oldCycleMonths,
      newCycleMonths,
      metadata: {
        oldCapacity: currentActiveRoom,
        newCapacity: values.activeRoom,
        oldPlanPrice: currentPlanData.price,
        newPlanPrice: selectedPlan.price,
      },
    });

    prorateAmount = prorateDetails.prorateCreditAmount;
    prorateChargeAmount = prorateDetails.prorateChargeAmount;
    netAmount = prorateDetails.netAmount;
  }

  // Calculate applied coupon discount
  const appliedDiscount = values.appliedDiscount;
  let promoDiscountAmount = 0;
  if (appliedDiscount) {
    if (appliedDiscount.category === "transaction_discount") {
      if (appliedDiscount.type === "percentage") {
        promoDiscountAmount = Math.round(subtotal * (appliedDiscount.value / 100));
      } else if (appliedDiscount.type === "fixed_amount") {
        promoDiscountAmount = Math.min(subtotal, appliedDiscount.value);
      }
    } else if (appliedDiscount.category === "room_discount") {
      let roomPriceDiscountAmount = 0;
      if (appliedDiscount.type === "percentage") {
        roomPriceDiscountAmount = Math.round(pricePerRoom * (appliedDiscount.value / 100));
      } else if (appliedDiscount.type === "fixed_amount") {
        roomPriceDiscountAmount = appliedDiscount.value;
      }
      const discountedPricePerRoom = Math.max(0, pricePerRoom - roomPriceDiscountAmount);
      promoDiscountAmount = (pricePerRoom - discountedPricePerRoom) * roomCount * cycleMonths;
    }
  }

  const discountAmount = promoDiscountAmount;
  // const total = Math.max(0, subtotal + prorateChargeAmount - discountAmount - prorateAmount);
  const total = Math.max(0, netAmount - discountAmount);

  const handleApplyPromo = useCallback(async () => {
    if (!values.discountCode) return;
    setPromoError("");
    setValidating(true);
    const { status, data } = await store.subscription.validateDiscount(values.discountCode, values.plan);
    setValidating(false);
    if (status === 200 && data) {
      setFieldValue("appliedDiscount", data);
      setPromoError("");
    } else {
      setFieldValue("appliedDiscount", null);
      setPromoError("Invalid discount code");
      // setPromoError(data?.error || t("Invalid promo code or not applicable."));
    }
  }, [values.discountCode, values.plan, store.subscription, setFieldValue, t]);

  const handleRemovePromo = useCallback(() => {
    setFieldValue("appliedDiscount", null);
    setFieldValue("discountCode", "");
    setPromoError("");
  }, [setFieldValue]);

  const handleCalculate = useCallback(() => {
    const currentSubscription = {
      pricePerRoom: subscription?.billing?.amount / subscription?.usage?.activeRoom,
      roomCapacity: subscription?.usage?.activeRoom,
      cycleMonths: subscription?.billing?.cycle,
    };
    const newSubscription = {
      pricePerRoom: pricePerRoom,
      roomCapacity: roomCount,
      cycleMonths: cycleMonths,
    };

    const result = calculateProrationValue({
      currentPeriodStart: subscription?.period?.start,
      currentPeriodEnd: subscription?.period?.end,
      currentSubscription,
      newSubscription,
      effectiveDate: new Date(),
    });
  }, [subscription, pricePerRoom, roomCount, cycleMonths, calculateProrationValue]);

  const bonus = selectedPeriod.bonus;

  const startDate = useMemo(() => {
    // If it's a renewal of the same plan, starts after the current one.
    let start = moment(subscription?.period?.end);
    // If it's an upgrade or new, starts now.
    if (processType === "upgrade" || processType === "upsize") {
      start = moment();
    }

    return start;
  }, [subscription, processType, values.plan]);

  const endDate = useMemo(() => {
    const isFreeToPaidPlan = store.subscription?.isFreePlan && values.plan !== "starter";
    const isUpgrade = processType === "upgrade" || processType === "upsize";
    let end = moment(startDate).add(cycleMonths * 30, "days");

    if (isUpgrade) {
      end = moment(subscription?.period?.end);
    } else if (processType === "downgrade" || processType === "downsize" || processType === "renewal") {
      if (cycleMonths > 1) {
        end = moment(startDate).add(cycleMonths, "months");
      }
      end = moment(startDate).add(cycleMonths * 30, "days");
    }
    if (bonus && !isFreeToPaidPlan) {
      if (bonus.freeMonths) end.add(bonus.freeMonths * 30, "days");
      if (bonus.freeWeeks) end.add(bonus.freeWeeks * 7, "days");
      if (bonus.freeDays) end.add(bonus.freeDays, "days");
    }
    return end;
  }, [store.subscription, values, startDate, processType, cycleMonths, bonus]);

  return (
    <Box className={classes.summaryCard}>
      <Typography className={classes.summaryTitle}>
        <CheckCircle color="primary" fontSize="small" />
        {t("Billing Summary")}
      </Typography>

      {/* <Box className={classes.summaryRow}>
        <Typography className="label">{t("Process")}</Typography>
        <Typography className="value">{t(SUBSCRIPTION_CHANGE_TYPE[processType])}</Typography>
      </Box> */}
      {/* <Divider style={{ margin: "16px 0" }} /> */}

      <Box className={classes.summaryRow}>
        <Typography className="label">{t("Plan")}</Typography>
        <Typography className="value">{selectedPlan.name}</Typography>
      </Box>

      <Box className={classes.summaryRow}>
        <Typography className="label">{t("Rooms")}</Typography>
        <Typography className="value">
          {roomCount} {t("Rooms")}
        </Typography>
      </Box>

      <Box className={classes.summaryRow}>
        <Typography className="label">{t("Billing Period")}</Typography>
        <Typography className="value">{t(selectedPeriod.label)}</Typography>
      </Box>

      <Box className={classes.summaryRow}>
        <Typography className="label">{t("Price per room")}</Typography>
        <Typography className="value">{formatNumber(pricePerRoom, "currency")}</Typography>
      </Box>

      {/* Discount Code Input Section */}
      {values.plan !== "starter" && (
        <Box mb={2}>
          {!appliedDiscount ? (
            <Box>
              <Typography
                variant="body2"
                className={classes.promoLabel}
                onClick={() => setShowPromoInput(!showPromoInput)}
              >
                {t("Have a billing code?")}
              </Typography>
              {showPromoInput && (
                <Box className={classes.promoInputContainer}>
                  <InputBase
                    placeholder={t("Enter code")}
                    className={classes.promoInput}
                    value={values.discountCode}
                    onChange={(e) => setFieldValue("discountCode", e.target.value.toUpperCase())}
                    disabled={validating}
                  />
                  <Button
                    variant="contained"
                    color="primary"
                    size="small"
                    className={classes.promoApplyBtn}
                    onClick={handleApplyPromo}
                    disabled={validating || !values.discountCode}
                  >
                    {validating ? <CircularProgress size={16} color="inherit" /> : t("Apply")}
                  </Button>
                </Box>
              )}
              {promoError && (
                <Typography color="error" variant="caption" style={{ display: "block", marginTop: 4 }}>
                  {t(promoError)}
                </Typography>
              )}
            </Box>
          ) : (
            <Box className={classes.appliedPromoChip}>
              <Typography className={classes.appliedPromoText}>
                {t("Code Applied {{code}}", { code: appliedDiscount.code })}
              </Typography>
              <IconButton size="small" onClick={handleRemovePromo} style={{ color: "#2E7D32", padding: 4 }}>
                <Close style={{ fontSize: 16 }} />
              </IconButton>
            </Box>
          )}
        </Box>
      )}

      <Divider style={{ margin: "16px 0" }} />

      <Box className={classes.summaryRow}>
        <Typography className="label">{t("Subtotal")}</Typography>
        <Typography className="value">{formatNumber(subtotal, "currency")}</Typography>
      </Box>

      {prorateChargeAmount > 0 && !store.subscription.isFreePlan && (
        <Box className={classes.summaryRow}>
          <Typography className="label" style={{ color: "#7d2e2eff", fontWeight: 700 }}>
            {t("Prorated Charge")}
          </Typography>
          <Typography className="value" style={{ color: "#7d2e2eff" }}>
            {formatNumber(prorateChargeAmount, "currency")}
          </Typography>
        </Box>
      )}

      {prorateAmount > 0 && !store.subscription.isFreePlan && (
        <Box className={classes.summaryRow}>
          <Typography className="label" style={{ color: "#2E7D32", fontWeight: 700 }}>
            {t("Prorated Credit")}
          </Typography>
          <Typography className="value" style={{ color: "#2E7D32" }}>
            -{formatNumber(prorateAmount, "currency")}
          </Typography>
        </Box>
      )}

      {discountAmount > 0 && (
        <Box className={classes.summaryRow}>
          <Typography className="label" style={{ color: "#2E7D32", fontWeight: 700 }}>
            {appliedDiscount ? t("Promo Discount") : t("Discount")}
          </Typography>
          <Typography className="value" style={{ color: "#2E7D32" }}>
            -{formatNumber(discountAmount, "currency")}
          </Typography>
        </Box>
      )}

      <Box className={classes.totalRow}>
        <Typography className="label">{t("Total Cost")}</Typography>
        <Typography className="value">{formatNumber(total, "currency")}</Typography>
      </Box>

      <Box className={classes.noteSection}>
        <Box className={classes.noteTitle}>
          <InfoOutlined style={{ fontSize: 14 }} />
          {t("Subscription Period")}
        </Box>
        <Typography className={classes.noteText}>
          {t("Your subscription will be effective from {{start}} until {{end}}.", {
            start: startDate.format("LL"),
            end: endDate.format("LL"),
          })}
          {bonus && (
            <span style={{ display: "block", marginTop: 4, fontWeight: 700, color: "#2E7D32" }}>
              {t("Includes bonus")}:{" "}
              {[
                bonus.freeMonths && `${bonus.freeMonths} ${t("months")}`,
                bonus.freeWeeks && `${bonus.freeWeeks} ${t("weeks")}`,
                bonus.freeDays && `${bonus.freeDays} ${t("days")}`,
              ]
                .filter(Boolean)
                .join(", ")}
            </span>
          )}
        </Typography>
      </Box>

      {selectedPeriod.discount > 0 && (
        <Chip
          label={t("You save {{amount}}", { amount: formatNumber(discountAmount, "currency") })}
          size="small"
          className={classes.savingsChip}
        />
      )}

      <Box mt={4}>
        <SubmitButton
          fullWidth
          size="large"
          label={t("Confirm and Update")}
          color="primary"
          disabled={!step4Valid}
          style={{ height: 56, borderRadius: 12, fontWeight: 800, fontSize: "1rem", opacity: step4Valid ? 1 : 0.55 }}
        />
        {!step4Valid && (
          <Typography variant="caption" color="error" align="center" display="block" style={{ marginTop: 8 }}>
            {t("Please complete Step 4 — schedule which rooms to disable before confirming.")}
          </Typography>
        )}
      </Box>
    </Box>
  );
};

const ManageSubscriptionForm = ({ subscription, onSubmit }) => {
  const { t } = useTranslation("common");
  const classes = useStyles();
  const store = useContext(StoreContext);
  const formatNumber = useFormatNumber();

  // Controlled state for delayed room pre-selection (Step 4)
  const [delayedRooms, setDelayedRooms] = useState(null);

  const plans = useMemo(() => {
    return STATIC_PLANS;
    return store.subscription.plans?.length > 0 ? store.subscription.plans : STATIC_PLANS;
  }, [store.subscription.plans]);

  const config = useMemo(() => {
    const { settings } = store.subscription.meta;

    const periods = settings.period?.map((p) => {
      const periodBonus = settings.freeBonus.find((fb) => fb.months == p.months);
      const bonusObj = periodBonus && {
        freeMonths: periodBonus.bonusMonths,
        freeWeeks: periodBonus.bonusWeeks,
        freeDays: periodBonus.bonusDays,
      };
      return {
        label: p.name,
        frequency: "months",
        cycle: p.months,
        bonus: bonusObj || null,
      };
    });

    return { periods, monthInPeriod: settings.monthLength };
  }, [store.subscription.settings]);

  // Rooms & properties from store (already fetched by manage-plan.jsx)
  const liveRooms = useMemo(
    () =>
      store.room.items?.map((r) => ({
        id: r._id,
        propertyId: r.property?.id || r.propertyId,
        name: r.name,
        enabled: r.isEnabled,
        vacant: r.room_occupancy === "vacant",
      })) ?? [],
    [store.room.items],
  );

  const liveProperties = useMemo(
    () => store.property.items?.map((p) => ({ id: p._id, name: p.name })) ?? [],
    [store.property.items],
  );

  const currentEnabledCount = useMemo(() => liveRooms.filter((r) => r.enabled).length, [liveRooms]);
  const currentPlan = subscription?.plan?.toLowerCase() || "starter";

  const initialValues = useMemo(() => {
    return {
      plan: currentPlan,
      frequency: subscription?.billing?.frequency || "months",
      cycle: subscription?.billing?.cycle || 1,
      activeRoom: Math.max(LIMIT_MIN, Number(subscription?.usage?.activeRoom) || LIMIT_MIN),
      discountCode: "",
      appliedDiscount: null,
    };
  }, [subscription]);

  const handleSubmit = useCallback(
    async (values) => {
      if (!onSubmit) return;

      const isStarterDownsize = values.plan === "starter" && currentPlan !== "starter";
      const isDownsizeWithExcess = values.activeRoom < currentEnabledCount;

      // Build delayedDisable payload:
      // - For starter: auto-disable all currently enabled rooms
      // - For regular downsize with excess: use user's selection from Step 4
      // - Otherwise: empty (no delayed disables needed)
      let delayedDisable = [];
      if (isStarterDownsize) {
        delayedDisable = liveRooms.filter((r) => r.enabled).map((r) => r.id);
      } else if (isDownsizeWithExcess && delayedRooms) {
        delayedDisable = delayedRooms.filter((r) => !r.enabled).map((r) => r.id);
      }

      await onSubmit({
        ...values,
        delayedDisable,
        discountCode: values.appliedDiscount?.code || null,
      });
    },
    [onSubmit, currentPlan, currentEnabledCount, liveRooms, delayedRooms],
  );

  return (
    <Formik initialValues={initialValues} enableReinitialize onSubmit={handleSubmit}>
      {({ values, setFieldValue }) => {
        // Derived downsize flags (inside Formik render for access to `values`)
        const isStarterDownsize = values.plan === "starter" && currentPlan !== "starter";
        const isDownsizeWithExcess = !isStarterDownsize && values.activeRoom < currentEnabledCount;

        // Reset delayed room selection whenever the target capacity changes
        // (user revised Step 3 after already selecting rooms in Step 4)
        // We use a key-trick via the DelayedRoomSelector re-mount instead of useEffect
        // to avoid stale closure issues inside Formik's render prop.

        // Step 4 is valid when:
        // - No excess: always valid
        // - Starter downsize: always valid (auto-populated in handleSubmit)
        // - Regular downsize with excess: valid when user has selected exactly `values.activeRoom` rooms to keep enabled
        const step4Valid =
          !isDownsizeWithExcess ||
          (delayedRooms !== null && delayedRooms.filter((r) => r.enabled).length <= values.activeRoom);

        return (
          <Form autoComplete="off">
            <Grid container spacing={4}>
              <Grid item xs={12} md={8}>
                {/* Plan Selection */}
                <Box mb={6}>
                  <Typography variant="subtitle1" className={classes.sectionTitle}>
                    1. {t("Select your plan")}
                  </Typography>
                  <Box className={classes.planGrid}>
                    {plans.map((p) => {
                      const selected = values.plan === p.key;
                      const isCurrent = subscription?.plan?.toLowerCase() === p.key;
                      return (
                        <Button
                          key={p.key}
                          type="button"
                          className={`${classes.planOption} ${selected ? classes.planOptionSelected : ""}`}
                          onClick={() => {
                            if (p.key === values.plan) return;
                            setFieldValue("plan", p.key);
                            if (p.key === "starter") {
                              setFieldValue("frequency", null);
                              setFieldValue("cycle", null);
                              setFieldValue("activeRoom", 0);
                            }
                          }}
                          classes={{
                            label: classes.planOptionLabel,
                          }}
                        >
                          {isCurrent && <Box className={classes.currentBadge}>{t("Current")}</Box>}
                          {selected && <CheckCircle className={classes.selectedBadge} style={{ fontSize: 18 }} />}
                          <Typography variant="subtitle2" className={classes.planOptionName}>
                            {p.name}
                          </Typography>
                          {/* <Typography variant="body2" className={classes.planOptionPrice}>
                          {p.price === 0 ? t("Free") : `${formatNumber(p.price, "currency")}/${t("room")}`}
                        </Typography> */}
                        </Button>
                      );
                    })}
                  </Box>
                </Box>

                {/* Period Selection */}
                <Box mb={6}>
                  <Typography variant="subtitle1" className={classes.sectionTitle}>
                    2. {t("Choose billing cycle")}
                  </Typography>
                  <Box className={classes.periodGrid}>
                    {config?.periods?.map((period) => {
                      const selected = values.frequency === period.frequency && values.cycle === period.cycle;
                      const isCurrent =
                        subscription?.billing?.frequency === period.frequency &&
                        subscription?.billing?.cycle === period.cycle;

                      return (
                        <Button
                          key={`${period.frequency}-${period.cycle}`}
                          type="button"
                          className={`${classes.planOption} ${selected ? classes.planOptionSelected : ""}`}
                          onClick={() => {
                            setFieldValue("frequency", period.frequency);
                            setFieldValue("cycle", period.cycle);
                          }}
                          classes={{
                            label: classes.planOptionLabel,
                          }}
                          disabled={values.plan === "starter"}
                        >
                          {isCurrent && <Box className={classes.currentBadge}>{t("Current")}</Box>}
                          {selected && <CheckCircle className={classes.selectedBadge} style={{ fontSize: 18 }} />}
                          <Typography variant="subtitle2" className={classes.planOptionName}>
                            {t(period.label)}
                          </Typography>
                          {period.bonus && (
                            <Typography variant="caption" className={classes.planOptionMeta}>
                              {period.bonus.freeMonths > 0 && `${t("Extra")} ${period.bonus.freeMonths} ${t("month")}`}
                              {period.bonus.freeWeeks > 0 && `${t("Extra")} ${period.bonus.freeWeeks} ${t("week")}`}
                              {period.bonus.freeDays > 0 && `${t("Extra")} ${period.bonus.freeDays} ${t("day")}`}
                            </Typography>
                          )}
                        </Button>
                      );
                    })}
                  </Box>
                  <Box className={classes.noteSection} mt={2}>
                    <Box className={classes.noteTitle}>
                      <InfoOutlined style={{ fontSize: 14 }} />
                      {t("Billing Cycle Note")}
                    </Box>
                    <Typography className={classes.noteText}>
                      {t("Please note that each months is calculated as {{count}} days.", {
                        count: config?.monthInPeriod || 30,
                      })}
                    </Typography>
                  </Box>
                </Box>

                {/* Room Selection */}
                <Box mb={4}>
                  <Typography variant="subtitle1" className={classes.sectionTitle}>
                    3. {t("Purchased Capacity")}
                  </Typography>
                  <Grid container>
                    <Grid item xs={12} sm={8}>
                      <Box className={classes.limitCard}>
                        <Box display="flex" alignItems="center" gap={1} mb={1}>
                          <Typography variant="body2" style={{ fontWeight: 700 }}>
                            {t("Number of Active Rooms")}
                          </Typography>
                          <InfoOutlined style={{ fontSize: 14, color: "rgba(0,0,0,0.4)" }} />
                        </Box>
                        <Typography
                          variant="caption"
                          color="textSecondary"
                          display="block"
                          style={{ marginBottom: 12 }}
                        >
                          {t("Increase your limit to enable more rooms for your properties.")}
                        </Typography>

                        <Box className={classes.limitStepperRow}>
                          <IconButton
                            type="button"
                            size="medium"
                            disabled={values.activeRoom <= LIMIT_MIN || values.plan === "starter"}
                            onClick={() => setFieldValue("activeRoom", Math.max(LIMIT_MIN, values.activeRoom - 1))}
                          >
                            <Remove />
                          </IconButton>
                          <InputBase
                            type="number"
                            className={classes.limitValue}
                            value={values.activeRoom}
                            onChange={(e) => {
                              const val = parseInt(e.target.value, 10);
                              if (!isNaN(val)) {
                                setFieldValue("activeRoom", Math.max(LIMIT_MIN, Math.min(LIMIT_MAX, val)));
                              }
                            }}
                            disabled={values.plan === "starter"}
                          />
                          <IconButton
                            type="button"
                            size="medium"
                            disabled={values.activeRoom >= LIMIT_MAX || values.plan === "starter"}
                            onClick={() => setFieldValue("activeRoom", Math.min(LIMIT_MAX, values.activeRoom + 1))}
                          >
                            <Add />
                          </IconButton>
                        </Box>
                      </Box>
                    </Grid>
                  </Grid>
                </Box>
                {/* Step 4: Delayed Room Pre-Selection — only shown when downsizing with excess enabled rooms */}
                {isDownsizeWithExcess && (
                  <Box mb={4}>
                    <Typography variant="subtitle1" className={classes.sectionTitle}>
                      4. {t("Synchronize Active Room")}
                    </Typography>
                    <Typography variant="body2" color="textSecondary" style={{ marginBottom: 16 }}>
                      {t(
                        "Your new limit is {{limit}} active room(s), but you currently have {{count}} enabled. Choose which rooms to disable on the effective date.",
                        { limit: values.activeRoom, count: currentEnabledCount },
                      )}
                    </Typography>
                    <DelayedRoomSelector
                      key={`delayed-${values.activeRoom}`}
                      rooms={liveRooms}
                      properties={liveProperties}
                      newLimit={values.activeRoom}
                      value={delayedRooms}
                      onChange={(updated) => setDelayedRooms(updated)}
                    />
                  </Box>
                )}

                {/* Step 4 (Starter): Warning note — no manual selection needed */}
                {isStarterDownsize && (
                  <Box mb={4}>
                    <Alert
                      severity="warning"
                      icon={<Warning fontSize="inherit" />}
                      variant="outlined"
                      style={{ borderRadius: 10 }}
                    >
                      <strong>{t("All rooms will be disabled on the effective date.")}</strong>{" "}
                      {t(
                        "Please ensure all tenants have ended their leases before that date. No manual room selection is required.",
                      )}
                    </Alert>
                  </Box>
                )}
              </Grid>

              {/* Sticky Sidebar Summary */}
              <Grid item xs={12} md={4}>
                <OrderSummary
                  plans={plans}
                  subscription={subscription}
                  periods={config?.periods}
                  step4Valid={step4Valid}
                />
              </Grid>
            </Grid>
          </Form>
        );
      }}
    </Formik>
  );
};

export default observer(ManageSubscriptionForm);
